import React, { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DEFAULT_LIST_PAGE_SIZE } from './constants';
import useUsers from './hooks/useUsers';
import IconButton from './components/IconButton';
import EditActionIcon from './components/EditActionIcon';
import PageHeader from './components/PageHeader';
import Pagination from './components/Pagination';
import SortableTableHeader from './components/SortableTableHeader';
import { getNextSortCriterion } from './utils/tableSort';
import type { UserRecord } from './types/index';

const statusLabelFromUser = (u: any): string => {
  if (u?.active === false) return 'Inactive';
  if (u?.verifiedEmail === false) return 'Pending Verification';
  return 'Active';
};

export default function UsersPage({ onAdd, onEdit, organizationId, organizationName, currentUser }) {

  const navigate = useNavigate();
  const descendingFirstFields = useRef(new Set(['lastModified']));
  const [page, setPage] = useState(0);
  const [paginationOrganizationId, setPaginationOrganizationId] = useState(organizationId);
  const [search, setSearch] = useState('');
  const pageSize = DEFAULT_LIST_PAGE_SIZE;
  const trimmedSearch = search.trim();
  const effectiveSearch = trimmedSearch.length >= 2 ? trimmedSearch : null;
  const [sort, setSort] = useState({ fieldName: 'name', isAscending: true });

  if (paginationOrganizationId !== organizationId) {
    setPaginationOrganizationId(organizationId);
    setPage(0);
  }

  const { data, total, isLoading, error, refetch } = useUsers({
    organizationId,
    page,
    pageSize,
    search: effectiveSearch,
    sortField: sort.fieldName,
    sortAscending: sort.isAscending,
  });

  const toggleSortDirection = useCallback((fieldName: string) => {
    setSort((current) => getNextSortCriterion(current, fieldName, descendingFirstFields.current));
    setPage(0);
  }, []);

  return (
    <div className="stack" data-testid="page-users">
      <button className="button" type="button" onClick={() => navigate('/admin')} data-testid="users-back-to-admin" style={{ alignSelf: 'flex-start' }}>
        Back
      </button>
      <PageHeader
        title="Users & Auth"
        subtitle="Access Control"
        actions={
          <div>
             {(currentUser?.roleActions?.indexOf('WriteUserManagement') >= 0 ||
               currentUser?.roleActions?.indexOf('AdminUserManagement') >= 0) && <button className="button cta" onClick={onAdd} data-testid="users-invite">
               <span className="plus" aria-hidden="true">
                 <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                   <path d="M3.33301 8H12.6663" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                   <path d="M8 3.33301V12.6663" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                 </svg>
               </span>
               Invite User
             </button>}
          </div>
        }
      />

      <div className="card filter-card" style={{ marginBottom: 12 }}>
        <div className="filter-bar">
          <div className="filter-left">
            <input
              className="input filter-search"
              type="search"
              placeholder="Search by Name or Email"
              aria-label="Search users by name or email"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              minLength={2}
              data-testid="users-search"
            />
          </div>
        </div>
      </div>

      {isLoading && data.length === 0 && (
        <div className="alert" role="status" data-testid="users-loading">
          Loading users…
        </div>
      )}
      {error && (
        <div className="alert error" role="alert" data-testid="users-load-error">
          <span>Users could not be loaded. Please try again.</span>
          <button type="button" className="button secondary" onClick={() => refetch()} data-testid="users-retry">
            Retry
          </button>
        </div>
      )}

      <div className="card" data-testid="users-table">
        <table className="table" data-testid="users-table-content">
          <thead>
            <tr>
              <SortableTableHeader label="Name" fieldName="name" sort={sort} onSort={toggleSortDirection} />
              <SortableTableHeader label="Email" fieldName="email" sort={sort} onSort={toggleSortDirection} />
              <SortableTableHeader label="Role" fieldName="roleName" sort={sort} onSort={toggleSortDirection} />
              <SortableTableHeader label="Organization" fieldName="organization" sort={sort} onSort={toggleSortDirection} />
              <SortableTableHeader label="Status" fieldName="status" sort={sort} onSort={toggleSortDirection} />
              <SortableTableHeader label="Last Updated" fieldName="lastModified" sort={sort} onSort={toggleSortDirection} />
              <th aria-label="actions" />
            </tr>
          </thead>
          <tbody>
            {data.map((u: UserRecord) => {
              const orgLabel = organizationName || '—';
              const statusLabel = statusLabelFromUser(u);
              return (
                <tr key={u.id} data-testid={`user-row-${u.id}`}>
                  <td>{u.name}</td>
                  <td>{u.email || '—'}</td>
                  <td>{u.roleName || ''}</td>
                  <td>{orgLabel}</td>
                  <td>{statusLabel}</td>
                  <td>{u.lastModified instanceof Date ? u.lastModified.toLocaleDateString() : u.lastModified ? new Date(u.lastModified).toLocaleDateString() : ''}</td>
                  <td style={{ textAlign: 'right' }}>
                    {(currentUser?.roleActions?.indexOf('WriteUserManagement') >= 0 ||
                      currentUser?.roleActions?.indexOf('AdminUserManagement') >= 0) && <IconButton title="Edit" ariaLabel={`Edit ${u.email || 'user'}`} onClick={() => onEdit && onEdit(u)} testId={`user-edit-${u.id}`}>
                      <EditActionIcon />
                    </IconButton>}
                  </td>
                </tr>
              );
            })}
            {effectiveSearch && !isLoading && !error && data.length === 0 && (
              <tr data-testid="users-empty-results">
                <td colSpan={7} aria-live="polite">No Results</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <Pagination
        page={page}
        totalPages={Math.ceil(total / pageSize)}
        onPrev={() => { setPage((current) => current - 1) }}
        onNext={() => { setPage((current) => current + 1) }}
        format="of"
        prevLabel="◀"
        nextLabel="▶"
        testId="users-pagination"
      />
    </div>
  );
}
